import { useNavigate } from 'react-router-dom';
import { EmptyState, PageHeader, Stack, t } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { Shell } from '../components/Shell';
import { useDisplayScope } from '../components/ScopePicker';
import { useEnvironmentFilter } from '../components/EnvironmentPicker';
import { ServerStateLegend, serverStateSpec } from '../components/ServerState';
import { healthColumn } from '../components/StateColumn';
import { ListTable } from '../components/ListTable';
import { api, unwrap } from '../lib/api';
import { shortDateTime } from '../lib/format';
import { useSession } from '../lib/session';

type Issue = fleetState.components['schemas']['Issue'];
const VIEWS = ['all', 'incident', 'unknown', 'to_update', 'maintenance'] as const;

/**
 * D72 « Alertes »: every cause of every server shown, its own health (D47) and
 * its applications' (D71), the gravest first, then the oldest; in the person's
 * scope and environment filter (D73). A row opens the server where it is said.
 * D120: one view per state, searched, filtered and paged by the server.
 */
export function AlertsPage() {
  const { enums } = useSession();
  const navigate = useNavigate();
  const scope = useDisplayScope();
  const env = useEnvironmentFilter();
  const stateLabel = (state: string) => enums.states.find(s => s.value === state)?.label ?? state;
  return (
    <Shell crumbs={[{ label: t('nav.alerts') }]}>
      <Stack gap={24}>
        <PageHeader title={t('alerts.title')} subtitle={t('alerts.subtitle')} />
        <ListTable<Issue>
          label={t('alerts.table')}
          queryKey={['issues', scope, env]}
          fetch={async query => unwrap(await api.fleetState.GET('/issues', { params: { query: { ...query, scope, env } as never } }))}
          views={{ label: t('alerts.views'), items: VIEWS.map(id => ({ id, label: id === 'all' ? t('alerts.all') : stateLabel(id) })) }}
          rowKey="id"
          defaultSort={{ key: 'severity', dir: 'asc' }}
          search={t('alerts.search')}
          facets={[
            { key: 'kind', label: t('alerts.columns.kind'), valueLabel: v => t(`alerts.kind.${v}`) },
            { key: 'client', label: t('alerts.columns.client') },
            { key: 'project', label: t('alerts.columns.project') },
          ]}
          onRowClick={i => navigate(`/servers/${encodeURIComponent(i.server)}?tab=${i.kind === 'server' ? 'health' : 'applications'}`)}
          empty={<EmptyState framed={false} icon="task_alt" title={t('alerts.empty')} />}
          columns={[
            healthColumn<Issue>({
              key: 'severity',
              legendLabel: t('servers.legend'), legendTitle: t('servers.legendTitle'), legend: <ServerStateLegend />,
              spec: i => serverStateSpec(enums, i.severity),
            }),
            { key: 'label', label: t('alerts.columns.label'), grow: true, minWidth: 280 },
            { key: 'kind', label: t('alerts.columns.kind'), width: 130, sortable: false, render: (i: Issue) => t(`alerts.kind.${i.kind}`) },
            { key: 'server', label: t('alerts.columns.server'), width: 220, mono: true },
            { key: 'client', label: t('alerts.columns.client'), width: 180, sortable: false, render: (i: Issue) => i.client ?? '—' },
            { key: 'project', label: t('alerts.columns.project'), width: 180, sortable: false, render: (i: Issue) => i.project ?? '—' },
            { key: 'since', label: t('alerts.columns.since'), width: 150, render: (i: Issue) => (i.since ? shortDateTime(i.since) : '—') },
          ]}
        />
      </Stack>
    </Shell>
  );
}
